/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useState } from 'react'
import { useServiceContext } from '../root'
import { useTranslation } from 'react-i18next'
import { getApiUrl, isDevelopment } from '~/services/environment'

interface EsignatureOptions {
  esignMsg?: string
  keepParentModalsOpen?: boolean
  isCrossDomain?: boolean
}

export interface ValidationResult {
  username?: string
  password?: string
  relayState?: string
  result?: any
}

const lastEsignatureWindow: { win: Window | null } = { win: null }

/**
 * Hook for eSignature authentication flows
 * Handles three authentication types:
 * 1. DSO (RCM) - Shows modal with username/password
 * 2. SSO (IMEDIDATA/CHECKMATE) - Opens popup window with SAML
 * 3. OKTA - Shows confirmation dialog, then SSO popup
 *
 * Migrated from: lego/component/esignature/useEsignature.jsx
 */
export const useEsignature = () => {
  const context = useServiceContext()
  const { t } = useTranslation()
  const [showDsoModal, setShowDsoModal] = useState(false)
  const [esignMessage, setEsignMessage] = useState('')
  const [dsoResolver, setDsoResolver] = useState<{
    resolve: (value: ValidationResult) => void
    reject: () => void
  } | null>(null)

  const CALLBACK_FILENAME_SUCCESS = 'imedidata-esignature-callback.html'
  const CALLBACK_FILENAME_CANCEL = 'imedidata-esignature-callback-cancel.html'

  /**
   * Main entry point for eSignature authentication
   * Routes to appropriate auth flow based on auth type
   */
  const signAction = (
    actionType: string,
    actionName: string,
    options?: EsignatureOptions
  ): Promise<ValidationResult> => {
    // Build message with common description prefix
    const esignMsg = t('ESIGNATURE_ACTION_COMMON_DESC') + ' ' + t(actionName)
    const propsWithMsg = { ...options, esignMsg }

    const authType = context.service.auth.getAuthType()

    // Route to appropriate authentication flow based on auth type
    if (authType === 'OKTA') {
      return showOktaEsignAuth(propsWithMsg)
    } else if (authType === 'IMEDIDATA' || authType === 'CHECKMATE') {
      return showSsoEsignAuth(propsWithMsg)
    } else {
      // RCM/DSO authentication - modal with username/password
      return showDsoEsignAuth(propsWithMsg)
    }
  }

  /**
   * OKTA authentication flow
   * Shows confirmation dialog first, then opens SSO popup
   */
  const showOktaEsignAuth = (props: EsignatureOptions): Promise<ValidationResult> => {
    return new Promise((resolve, reject) => {
      // Show confirmation dialog
      const confirmed = window.confirm(
        `${t('ESIGNATURE_MODAL_TITLE')}\n\n${props.esignMsg}\n\n${t('OK')} / ${t('CANCEL')}`
      )

      if (confirmed) {
        showSsoEsignAuth(props)
          .then((validation) => {
            resolve(validation)
          })
          .catch(() => {
            reject()
          })
      } else {
        reject()
      }
    })
  }

  /**
   * DSO (RCM) authentication flow
   * Shows modal with username/password fields
   * Returns promise that resolves when modal is submitted
   */
  const showDsoEsignAuth = (props: EsignatureOptions): Promise<ValidationResult> => {
    return new Promise((resolve, reject) => {
      setEsignMessage(props.esignMsg || '')
      setShowDsoModal(true)
      setDsoResolver({ resolve, reject })
    })
  }

  /**
   * Handle DSO modal submission
   */
  const handleDsoSubmit = (validation: ValidationResult) => {
    setShowDsoModal(false)
    if (dsoResolver) {
      dsoResolver.resolve(validation)
      setDsoResolver(null)
    }
  }

  /**
   * Handle DSO modal close/cancel
   */
  const handleDsoClose = () => {
    setShowDsoModal(false)
    if (dsoResolver) {
      dsoResolver.reject()
      setDsoResolver(null)
    }
  }

  /**
   * SSO (IMEDIDATA/CHECKMATE) authentication flow
   * Opens popup window with SAML authentication
   */
  const showSsoEsignAuth = (props: EsignatureOptions): Promise<ValidationResult> => {
    let operationResult: any = {}
    let win: Window | null = null

    // Close any previous esignature window
    if (lastEsignatureWindow && lastEsignatureWindow.win && !lastEsignatureWindow.win.closed) {
      lastEsignatureWindow.win.close()
      lastEsignatureWindow.win = null
    }

    return new Promise((resolve, reject) => {
      // Event listener for popup window messages
      const reactToEsignResponse = (e: MessageEvent | CustomEvent) => {
        // Handle both standard MessageEvent and IE CustomEvent
        const evt = (e as MessageEvent).data && (e as MessageEvent).data.esignature ? e : (e as any).detail

        if (evt && evt.data && evt.data.esignature) {
          operationResult = evt.data
          window.removeEventListener('message', reactToEsignResponse as any)

          // Close popup after receiving message
          if (win && !win.closed) {
            win.close()
          }
        }
      }

      // IE compatibility - allow callback page to post message via custom function
      ;(window as any).postMessageFromOtherWindow = (esigType: string, relayState: string, loc: string) => {
        const event = new CustomEvent('message', {
          detail: {
            origin: loc,
            data: {
              relayState: relayState,
              esignature: esigType
            }
          }
        })
        window.dispatchEvent(event)
      }

      // Calculate popup window position (centered on screen)
      const authType = context.service.auth.getAuthType()
      const w = authType === 'OKTA' ? 370 : 300
      const h = authType === 'OKTA' ? 560 : 400

      const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : (screen as any).left
      const dualScreenTop = window.screenTop !== undefined ? window.screenTop : (screen as any).top
      const width = window.innerWidth || document.documentElement.clientWidth || screen.width
      const height = window.innerHeight || document.documentElement.clientHeight || screen.height
      const left = width / 2 - w / 2 + dualScreenLeft
      const top = height / 2 - h / 2 + dualScreenTop

      // Open popup window
      win = window.open('', 'esignature', `width=${w}, height=${h}, top=${top}, left=${left}`)

      // Store the window reference globally
      lastEsignatureWindow.win = win

      // Get eSignature SAML data from API (legacy parity)
      const tenantUuid = context.service.auth.getTenant() || localStorage.getItem('tenantUuid') || ''
      const isCheckmated = context.service.checkmate.isCheckmated()
      // Use environment variables and getApiUrl for config values
      const apiUrl = import.meta.env.VITE_API_URL || getApiUrl()
      const cdnUrl = import.meta.env.VITE_CDN_URL || ''
      const uiUrlEnv = import.meta.env.VITE_UI_URL || ''
      // Derive contextPathUriName from apiUrl (robust for edge-rcm)
      let contextPathUriName = ''
      if (apiUrl) {
        // Try to extract "edge-rcm" from "/apps/edge-rcm/be" or similar
        const match = apiUrl.match(/\/apps\/([^/]+)\/be/)
        contextPathUriName = match ? match[1] : 'edge-rcm'
      } else {
        contextPathUriName = 'edge-rcm'
      }
      // Build UI URL (always include /app for checkmated)
      let uiUrl = ''
      if (isCheckmated) {
        uiUrl = location.origin + '/apps/' + contextPathUriName + '/app'
      } else {
        uiUrl = isDevelopment() ? uiUrlEnv : document.documentElement.getAttribute('ui-url') || ''
      }
      // Add c_selections to callback URLs
      const cSelections = localStorage.getItem('c_selections') || ''
      const addCSelections = (url: string) => {
        if (!cSelections) return url
        return url.includes('?') ? `${url}&c_selections=${cSelections}` : `${url}?c_selections=${cSelections}`
      }
      const okHandlerUrl = context.service.checkmate.addCSelections
        ? context.service.checkmate.addCSelections(`${uiUrl}/${CALLBACK_FILENAME_SUCCESS}`)
        : addCSelections(`${uiUrl}/${CALLBACK_FILENAME_SUCCESS}`)
      const cancelHandlerUrl = context.service.checkmate.addCSelections
        ? context.service.checkmate.addCSelections(`${uiUrl}/${CALLBACK_FILENAME_CANCEL}`)
        : addCSelections(`${uiUrl}/${CALLBACK_FILENAME_CANCEL}`)
      // restPreferences (legacy, not used in fetch but for parity)
      // const restPreferences = isCheckmated && props.isCrossDomain ? { baseUrl: apiUrl, disabledCSelections: true } : {}
      // Use baseApiService for API call
      import('../services/api/base')
        .then(({ baseApiService }) => {
          return baseApiService.fetch<any>('/api/esignature/createEsignatureData', {
            params: {
              tenantUuid,
              esignatureMessage: props.esignMsg || '',
              okDialogHandlerUrl: okHandlerUrl,
              cancelDialogHandlerUrl: cancelHandlerUrl
            }
          })
        })
        .then((data: any) => {
          const relayState = data.relayState

          // Create SAML form in popup window
          if (win && !win.closed) {
            win.focus()

            const form = win.document.createElement('form')
            form.setAttribute('method', 'post')
            form.setAttribute('action', data.esignatureUrl)

            const inputRelayState = win.document.createElement('input')
            inputRelayState.type = 'hidden'
            inputRelayState.name = 'RelayState'
            inputRelayState.value = data.relayState
            form.appendChild(inputRelayState)

            const inputSamlRequest = win.document.createElement('input')
            inputSamlRequest.type = 'hidden'
            inputSamlRequest.name = 'SAMLRequest'
            inputSamlRequest.value = data.samlRequest
            form.appendChild(inputSamlRequest)

            win.document.body.appendChild(form)
            form.submit()

            // Listen for window messages from callback page
            window.addEventListener('message', reactToEsignResponse as any, false)

            // Poll for window close and check for result
            const checkWindowClosed = setInterval(() => {
              if (win && win.closed) {
                clearInterval(checkWindowClosed)

                // Give a brief delay for any pending messages to arrive
                setTimeout(() => {
                  window.removeEventListener('message', reactToEsignResponse as any)

                  // Clean up IE compatibility function
                  delete (window as any).postMessageFromOtherWindow

                  if (operationResult && operationResult.esignature === 'verified') {
                    resolve({
                      relayState: relayState,
                      result: operationResult
                    })
                  } else {
                    reject()
                  }
                }, 200)
              }
            }, 500)
          } else {
            reject()
          }
        })
        .catch(() => {
          if (win && !win.closed) {
            win.close()
          }
          reject()
        })
    })
  }

  return {
    signAction,
    showDsoModal,
    esignMessage,
    handleDsoSubmit,
    handleDsoClose
  }
}
